import React, { KeyboardEvent, useRef, useState } from 'react';
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    ProfileSettingsWorkHourPrice,
    WorkHourPriceCurrency,
    WorkHourPriceType
} from 'src/model/api/v2/data-contracts';
import { useSaveWorkHourPricesMutation } from 'src/api/v2/profile-settings-v2-ApiSlice';
import { SettingsSection } from 'src/pages/ProjectSettingsPage/components/SettingsSection';
import { ServiceFeeIcon } from 'src/icons/ServiceFeeIcon';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';

type FeeValues = Record<WorkHourPriceType, string>;
type FeeNumbers = Record<WorkHourPriceType, number>;

const BLOCKED_KEYS = ['e', 'E', 'ArrowUp', 'ArrowDown'];
const EMPTY_VALUES: FeeValues = {
    [WorkHourPriceType.Installation]: '',
    [WorkHourPriceType.Personalisation]: '',
};

const feeLabel = (type: WorkHourPriceType) => type === WorkHourPriceType.Installation
    ? ts('profile_settings_installation_fee', 'Installation fee / hour')
    : ts('profile_settings_personalisation_fee', 'Personalisation fee / hour');

const toInputValues = (prices: ProfileSettingsWorkHourPrice[], currency: WorkHourPriceCurrency) =>
    Object.fromEntries(prices.map(price => [price.type, price[currency].toString()])) as FeeValues;

const toFees = (values: FeeValues): FeeNumbers => ({
    [WorkHourPriceType.Installation]: Number(values[WorkHourPriceType.Installation]) || 0,
    [WorkHourPriceType.Personalisation]: Number(values[WorkHourPriceType.Personalisation]) || 0,
});

const isSameFees = (a: FeeNumbers, b: FeeNumbers) =>
    Object.values(WorkHourPriceType).every(type => a[type] === b[type]);

type Props = {
    prices: ProfileSettingsWorkHourPrice[];
    currencyName: string;
    infoLink: string;
};

export const ServiceFeeSection = ({ prices, currencyName, infoLink }: Props) => {
    const dispatch = useAppDispatch();
    const [saveWorkHourPrices] = useSaveWorkHourPricesMutation();
    const currency = Object.values(WorkHourPriceCurrency).find(code => code === currencyName.toLowerCase());
    const [values, setValues] = useState<FeeValues>(() => currency ? toInputValues(prices, currency) : EMPTY_VALUES);
    // compared to what was sent last, the server value differs from the typed text after its currency conversion
    const lastSentFees = useRef<FeeNumbers>(toFees(values));

    if (!currency) return null;

    const handleBlur = () => {
        const fees = toFees(values);
        if (isSameFees(fees, lastSentFees.current)) {
            return;
        }

        const previousFees = lastSentFees.current;
        lastSentFees.current = fees;
        saveWorkHourPrices({
            currency,
            installation: fees[WorkHourPriceType.Installation],
            personalisation: fees[WorkHourPriceType.Personalisation],
        })
            .unwrap()
            .then(() => dispatch(showSnackBar({
                text: ts('profile_details_saved', 'Profile details saved!'),
                severity: 'success',
            })))
            .catch((error) => {
                lastSentFees.current = previousFees;
                if (error?.data?.errorMessage == null) {
                    dispatch(showSnackBar({
                        text: ts('profile_settings_save_error', 'Failed to save profile settings'),
                        severity: 'error',
                    }));
                }
            });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (BLOCKED_KEYS.includes(event.key)) event.preventDefault();
    };

    return (
        <SettingsSection
            title={ts('profile_settings_service_fee', 'Service fee setting')}
            titleIcon={<ServiceFeeIcon />}
            titleAction={(
                <Tooltip title={infoLink}>
                    <IconButton size="small" color="primary" href={infoLink} target="_blank" rel="noopener noreferrer">
                        <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{ts('profile_settings_hourly_fees', 'Hourly fees')}</TableCell>
                        <TableCell align="right">{currency.toUpperCase()}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {prices.map(price => (
                        <TableRow key={price.type}>
                            <TableCell>{feeLabel(price.type)}</TableCell>
                            <TableCell align="right">
                                <TextField
                                    type="number"
                                    size="small"
                                    value={values[price.type]}
                                    slotProps={{
                                        htmlInput: { min: 0, step: currency === WorkHourPriceCurrency.Huf ? 1 : 0.01 },
                                    }}
                                    onChange={(event) => setValues(prevValues => ({ ...prevValues, [price.type]: event.target.value }))}
                                    onBlur={handleBlur}
                                    onKeyDown={handleKeyDown}
                                    onWheel={(event) => (event.target as HTMLInputElement).blur()}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </SettingsSection>
    );
};
